import React, { Component } from "react";
import axios from "axios";

import "../../components/tabsApps/style/style.css";
import withOutsideClick from "../../hocs/withOutsideClick";
import { connect } from "react-redux";
import { PowerPointIcon, DownloadIcon, SparklesIcon } from "../../lib/icons";
import { saveCroppedImage, getSlides, updateSlidesOrder, deleteSlides, getSlidesForExport, retryPPTCartBlobCleanup } from "./pptCartReducer";
import PptxGenJS from "pptxgenjs";
import "../../components/profile/style/style.css";
import "./style.css";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CartItem } from "../../components/pptCart/components/cartItem";
import { getSlideIds, reorderSlides } from "./slideSorting";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import withRouter from "../../hocs/withRouter";
import { socket } from "../../routes";
import { trackAiEvent } from "../../lib/aiTelemetryClient";
import { DotLoader } from "react-spinners";

const grid = 8;
const verticalDragModifiers = [({ transform }) => ({ ...transform, x: 0 })];

const listStyle = {
  background: "white",
  padding: grid,
  paddingLeft: "0px",
  paddingRight: "0px",
  width: "100%",
  maxHeight: "580px",
  overflowY: "scroll",
};

function SortableSlideList({ slides, onReorder, deleteHandler }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = getSlideIds(slides);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }
    onReorder(ids.indexOf(active.id), ids.indexOf(over.id));
  };

  return (
    <DndContext sensors={sensors} modifiers={verticalDragModifiers} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="slide-list" style={listStyle} data-testid="ppt-cart-slide-list">
          {slides.map((item, index) => (
            <CartItem deleteHandler={deleteHandler} key={ids[index]} id={ids[index]} item={item} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

// Generate AI summaries for slides
const generateSlideSummaries = async (slides, onProgress) => {
  // Bug fix 1: send `data` field (not `imageBase64`) — server reads slide.data
  const slideData = slides.map((slide, index) => ({
    slideNumber: index + 1,
    data: slide.data,
    name: slide.name || `Slide ${index + 1}`,
  }));

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BIGHORN_SERVER}/api/app/generateSlideSummaries`,
      {
        slides: slideData,
        stream: false,
      },
      {
        headers: {
          ClientToken: localStorage.getItem("clientToken"),
          "Content-Type": "application/json",
        },
      },
    );

    // Bug fix 2: server wraps summaries under response.data.data.summaries
    const summaries = response.data?.data?.summaries;
    if (Array.isArray(summaries)) {
      return summaries;
    }
    return [];
  } catch (error) {
    console.error("Error generating slide summaries:", error);
    return [];
  }
};

class PPTCart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      listOpen: false,
      exporting: false,
      retryingCleanup: false,
      generateSummaries: false, // Checkbox state for AI summary generation
      exportStatus: "", // Status message during export
      //items: [{ name: "Download To Powerpoint",icon:"DownloadIcon" action: "logout" }]
    };
    //this.login = this.login().bind(this);
    this.downloadPPT = this.downloadPPT.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.deleteHandler = this.deleteHandler.bind(this);
    this.deleteAllHandler = this.deleteAllHandler.bind(this);
    this.retryBlobCleanup = this.retryBlobCleanup.bind(this);
    this.checkForIframeClick = this.checkForIframeClick.bind(this);
    this.toggleGenerateSummaries = this.toggleGenerateSummaries.bind(this);
    this.handleDownloadAction = this.handleDownloadAction.bind(this);
    this.progressPollInterval = null;
    this.refreshTimeout = null;
    window.addEventListener("blur", this.checkForIframeClick, false);
  }
  componentDidMount() {
    this.registerSocketListener();
    if (this.props.apps.length > 0 && !this.props.loaded) {
      this.props.getSlides();
    }
    this.updateProgressPolling(this.props.slides);
  }
  componentDidUpdate() {
    const { apps, loaded } = this.props;
    if (apps.length > 0 && !loaded) {
      this.props.getSlides();
    }
    this.updateProgressPolling(this.props.slides);
    // Retry registering socket listener if it wasn't available on mount
    if (!this.socketListenerRegistered) {
      this.registerSocketListener();
    }
  }
  componentWillUnmount() {
    window.removeEventListener("blur", this.checkForIframeClick, false);
    this.stopProgressPolling();
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
    // Clean up socket listener
    if (socket) {
      socket.off("DownloadAction", this.handleDownloadAction);
    }
  }
  registerSocketListener() {
    if (socket && !this.socketListenerRegistered) {
      socket.on("DownloadAction", this.handleDownloadAction);
      this.socketListenerRegistered = true;
    }
  }
  handleDownloadAction() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
    this.refreshTimeout = setTimeout(() => {
      this.refreshTimeout = null;
      this.props.getSlides(true);
    }, 500);
  }
  updateProgressPolling(slides) {
    const hasPendingExport = slides.some((slide) => slide.status === "pending");
    if (hasPendingExport && !this.progressPollInterval) {
      this.progressPollInterval = setInterval(() => {
        this.props.getSlides(true);
      }, 3000);
    } else if (!hasPendingExport) {
      this.stopProgressPolling();
    }
  }
  stopProgressPolling() {
    if (this.progressPollInterval) {
      clearInterval(this.progressPollInterval);
      this.progressPollInterval = null;
    }
  }
  renderActivityIndicator(slides) {
    if (!slides.some((slide) => slide.status === "pending")) {
      return null;
    }

    return (
      <span className="ppt-cart-activity ppt-cart-activity-running" role="status" aria-label="PowerPoint export in progress">
        <DotLoader size={15} color={"#CC7E00"} loading={true} />
      </span>
    );
  }
  checkForIframeClick = (event) => {
    if (document.activeElement.tagName === "IFRAME") {
      this.setState({
        listOpen: false,
      });
    }
  };

  handleClickOutside() {
    this.setState({
      listOpen: false,
    });
  }
  toggleGenerateSummaries() {
    if (!this.state.generateSummaries) trackAiEvent("ppt_slide_summaries", "ai_summary_enabled");
    this.setState((prevState) => ({ generateSummaries: !prevState.generateSummaries }));
  }

  // Helper to add a summary slide to the presentation
  addSummarySlide(pptx, title, bullets) {
    const slide = pptx.addSlide();

    // Add title
    slide.addText(title, {
      x: 0.5,
      y: 0.3,
      w: 9,
      h: 0.6,
      fontSize: 24,
      bold: true,
      color: "007EA7", // Brand color
    });

    // Add "AI Summary" label
    slide.addText("AI-Generated Summary", {
      x: 0.5,
      y: 0.9,
      w: 9,
      h: 0.3,
      fontSize: 12,
      italic: true,
      color: "666666",
    });

    // Add bullet points
    const bulletText = bullets.map((bullet) => ({
      text: bullet,
      options: { bullet: true, indentLevel: 0 },
    }));

    slide.addText(bulletText, {
      x: 0.5,
      y: 1.4,
      w: 9,
      h: 4,
      fontSize: 16,
      color: "333333",
      valign: "top",
      paraSpaceAfter: 8,
    });

    return slide;
  }

  async downloadPPT() {
    if (this.state.exporting) return; // Prevent double-clicks

    this.setState({ exporting: true, exportStatus: "Preparing slides..." });

    try {
      // Fetch slide data with images only when user clicks download
      const slides = await getSlidesForExport();

      if (!slides || slides.length === 0) {
        console.warn("No slides to export");
        this.setState({ exporting: false, exportStatus: "" });
        return;
      }

      // Generate AI summaries if checkbox is checked
      let summaries = [];
      if (this.state.generateSummaries) {
        this.setState({ exportStatus: "Generating AI summaries..." });
        summaries = await generateSlideSummaries(slides);
      }

      this.setState({ exportStatus: "Building PowerPoint..." });

      let dpi_y = document.getElementById("testdiv").offsetHeight;
      let dpi_x = document.getElementById("testdiv").offsetWidth;
      var pptx = new PptxGenJS();

      slides.forEach((result, index) => {
        let height, width, options, x, y;

        if (result.type !== undefined && result.type === "pbi") {
          height = result.h;
          width = result.w;
          options = {
            data: result.data,
            x: 0,
            y: 0,
            h: "100%",
            w: "100%",
            sizing: { type: "contain" },
          };
          let slide = pptx.addSlide();
          slide.addImage(options);
        } else {
          const max_width = 10; //slide width (in) for 16:9
          const max_height = 5.625; //slide height (in) for 16:9
          const scale_factor = "scaleFactor" in result ? result.scaleFactor : 1;
          x = result.x / dpi_x;
          y = result.y / dpi_y;
          height = result.h / dpi_y;
          width = result.w / dpi_y;
          let size_width = 0;
          let size_height = 0;
          if (width > max_width || height > max_height) {
            //check to see if the image is too big for our slide size
            let aspectRatio = width / height;
            if (aspectRatio === 0 || !aspectRatio) {
              aspectRatio = 1;
            }
            //figure out which dimension is most out-of-bounds
            if (height / max_height > width / max_width) {
              //height is furthest out of bounds so use it as reference to scale
              size_height = height / (height / max_height);
              size_width = size_height * aspectRatio; //multiply by aspectRatio because we computed aspectRatio as width/height
            } else if (height / max_height < width / max_width) {
              //width is furthest out of bounds so use it as reference to scale
              size_width = width / (width / max_width);
              size_height = size_width / aspectRatio; //divide by aspectRatio because we computed aspectRatio as width/height
            } else {
              //ignore the aspect ratio if perfect square
              size_height = height / (height / max_height);
              size_width = width / (width / max_width);
            }
          } else {
            //don't scale if it is within slide bounds
            size_width = width;
            size_height = height;
          }
          size_width = size_width * scale_factor;
          size_height = size_height * scale_factor;

          if (!result.useManualCrop || result.userCrop === true) {
            //if were not using manual settings, center the image, including userCrop
            x = (max_width - size_width) / 2;
            y = (max_height - size_height) / 2;
          }
          options = {
            data: result.data,
            x,
            y,
            w: size_width,
            h: size_height,
          };
          let slide = pptx.addSlide();
          slide.addImage(options);
        }

        // Add summary slide after this slide if summaries are enabled
        if (this.state.generateSummaries && summaries[index]) {
          const summary = summaries[index];
          // Bug fix 5: only insert summary slide if there are actual bullets to show
          const bullets = summary.bullets;
          if (Array.isArray(bullets) && bullets.length > 0) {
            // Use the server-cleaned slideName (timestamp/extension stripped)
            // rather than result.name, which still carries the "-YYYY-MM-DD-HHmmss.ext" suffix.
            const slideTitle = summary.slideName || result.name || `Slide ${index + 1}`;
            this.addSummarySlide(pptx, `Summary: ${slideTitle}`, bullets);
          }
        }
      });

      pptx.writeFile({ fileName: "TABS-PPT-Export" });
    } catch (error) {
      // swallow export errors
    } finally {
      this.setState({ exporting: false, exportStatus: "" });
    }
  }
  toggleList() {
    this.setState((prevState) => ({
      listOpen: !prevState.listOpen,
    }));
  }
  onDragEnd = (sourceIndex, destinationIndex) => {
    if (sourceIndex < 0 || destinationIndex < 0 || sourceIndex === destinationIndex) {
      return;
    }
    const slides = reorderSlides(this.props.slides, sourceIndex, destinationIndex);
    this.props.updateSlidesOrder(slides);
  };
  deleteHandler(item) {
    let slides = Array.from(this.props.slides);
    slides.splice(item.order, 1);
    slides = slides.map((item, index) => {
      item.order = index;
      return item;
    });
    this.props.deleteSlides(slides, item);
    //console.log(item);
  }
  deleteAllHandler() {
    this.props.deleteSlides([]);
  }

  async retryBlobCleanup() {
    this.setState({ retryingCleanup: true });
    try {
      await this.props.retryPPTCartBlobCleanup();
    } catch (error) {
      console.error("Failed to retry PowerPoint image cleanup:", error);
    } finally {
      this.setState({ retryingCleanup: false });
    }
  }

  render() {
    const { listOpen } = this.state;
    const { slides } = this.props;
    let showPPTCart = false;
    let proceed = this.props.apps.filter((application) => {
      return application.type === "tableau" || application.type === "powerBI";
    });
    if (proceed.length >= 1) {
      showPPTCart = true;
    }
    if (showPPTCart) {
      return (
        <div className={"flex-container"}>
          <OverlayTrigger key={"ppt-tip"} placement={"left"} overlay={<Tooltip id={"ppt-tip"}>Manage PowerPoint Deck</Tooltip>}>
            <div className="ppt-cart-parent" data-testid="ppt-cart-toggle" onClick={() => this.toggleList()}>
              {this.state.listOpen ? (
                <div className="ppt-cart-selected">
                  <span className="ppt-cart-icon-wrap">
                    <PowerPointIcon color={"white"} height={"20"} className="power-point-icon-selected" />
                    {this.renderActivityIndicator(slides)}
                  </span>
                </div>
              ) : (
                <div className="ppt-cart">
                  <span className="ppt-cart-icon-wrap">
                    <PowerPointIcon color={"rgba(88, 89, 91, 1)"} height={"20"} className="power-point-icon" />
                    {this.renderActivityIndicator(slides)}
                  </span>
                </div>
              )}
              {this.props.count > 0 && !slides.some((slide) => slide.status === "pending") ? (
                <span className="ppt-cart-badge">{this.props.count}</span>
              ) : null}
            </div>
          </OverlayTrigger>

          {listOpen && (
            <div className="ppt-cart-dd-items">
              <div className="ppt-btn-container">
                <div
                  className={`ppt-download-button ${this.state.exporting ? "ppt-download-button-disabled" : ""}`}
                  onClick={() => this.downloadPPT()}>
                  {this.state.exporting ? (
                    <>
                      <span className="ppt-spinner"></span>
                      {this.state.exportStatus || "Preparing Download..."}
                    </>
                  ) : (
                    <>
                      <DownloadIcon height={"18"} color={"rgba(255, 255, 255, 1)"} className="download-ppt-icon" />
                      Download PowerPoint
                    </>
                  )}
                </div>
              </div>
              <div className="ppt-ai-summary-option">
                <label className="ppt-ai-summary-label" data-testid="ppt-ai-summary-label">
                  <input
                    type="checkbox"
                    checked={this.state.generateSummaries}
                    onChange={this.toggleGenerateSummaries}
                    disabled={this.state.exporting}
                    className="ppt-ai-summary-checkbox"
                    data-testid="ppt-ai-summary-checkbox"
                  />
                  <SparklesIcon height={"14"} color={"rgba(0, 126, 167, 1)"} className="ppt-ai-summary-icon" />
                  <span>Auto-generate summary slides</span>
                </label>
                <div className="ppt-ai-summary-disclaimer" data-testid="ppt-ai-summary-disclaimer">
                  This feature uses AI, please verify results
                </div>
              </div>
              {this.props.pendingCleanupCount > 0 && (
                <div className="ppt-cleanup-warning" role="status">
                  <span>
                    {this.props.pendingCleanupCount} removed image
                    {this.props.pendingCleanupCount === 1 ? "" : "s"} could not
                    be deleted from storage.
                  </span>
                  <button
                    type="button"
                    onClick={this.retryBlobCleanup}
                    disabled={this.state.retryingCleanup}
                  >
                    {this.state.retryingCleanup ? "Retrying..." : "Retry cleanup"}
                  </button>
                </div>
              )}
              <div className="slides-container">
                <div className="slides-header">
                  <div>Slides</div>
                  <div className="clear-all" onClick={this.deleteAllHandler}>
                    clear all
                  </div>
                </div>
                <SortableSlideList slides={slides} onReorder={this.onDragEnd} deleteHandler={this.deleteHandler} />
              </div>
            </div>
          )}
        </div>
      );
    } else {
      return <div></div>;
    }
  }
}

const mapStateToProps = (state) => ({
  slides: state.pptCart.slides,
  count: state.pptCart.count,
  pendingCleanupCount: state.pptCart.pendingCleanupCount,
  apps: state.app.apps,
  loading: state.pptCart.loading,
  isAuthed: state.auth.isAuthed,
  loaded: state.pptCart.loaded,
});
const mapDispatchToProps = (dispatch) => ({
  saveCroppedImage(img) {
    dispatch(saveCroppedImage(img));
  },
  getSlides(forceRefresh = false) {
    dispatch(getSlides(forceRefresh));
  },
  updateSlidesOrder(slides) {
    dispatch(updateSlidesOrder(slides));
  },
  deleteSlides(slides, item = null) {
    dispatch(deleteSlides(slides, item));
  },
  retryPPTCartBlobCleanup() {
    return dispatch(retryPPTCartBlobCleanup());
  },
});

PPTCart = withOutsideClick(PPTCart);
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(PPTCart));
